import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Container, Nav, Table, Modal, Alert } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { database } from '../firebase';
import { ref, onValue, set, push, remove } from 'firebase/database';
import { Link, useLocation } from 'react-router-dom';

interface WorkLocation {
  id: string;
  name: string;
  color: string;
  is_active?: boolean;
}

interface TransportMeans {
  id: string;
  name: string;
  vega: boolean;
}

const WorkSettings = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const [workLocations, setWorkLocations] = useState<WorkLocation[]>([]);
  const [newLocation, setNewLocation] = useState({ name: '', color: '#1780a6', is_active: true });
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState<WorkLocation | null>(null);
  const [alertMessage, setAlertMessage] = useState<{ type: string; message: string } | null>(null);

  const [meansOfTransport, setMeansOfTransport] = useState<TransportMeans[]>([]);
  const [newTransport, setNewTransport] = useState('');
  const [showAddTransportModal, setShowAddTransportModal] = useState(false);
  const [editingTransport, setEditingTransport] = useState<TransportMeans | null>(null);

  useEffect(() => {
    if (!currentUser) return;

    const locationsRef = ref(database, `work/${currentUser.uid}/work_locations`);
    const unsubscribeLocations = onValue(locationsRef, (snapshot) => {
      const data = snapshot.val() || {};
      const locationsList = Object.entries(data).map(([id, location]: [string, any]) => ({
        id,
        ...location
      }));
      setWorkLocations(locationsList);
    });

    const meansRef = ref(database, `work/${currentUser.uid}/work_meansoftransport`);
    const unsubscribeMeans = onValue(meansRef, (snapshot) => {
      const data = snapshot.val() || {};
      const transportList: TransportMeans[] = Object.entries(data).map(([id, transport]: [string, any]) => ({
        id,
        name: transport.name || '',
        vega: transport.vega || false
      }));
      setMeansOfTransport(transportList);
    });

    return () => {
      unsubscribeLocations();
      unsubscribeMeans();
    };

  }, [currentUser]);

  const handleToggleActive = async (id: string, isActive: boolean) => {
    if (!currentUser) return;

    const current = workLocations.find((loc) => loc.id === id);
    if (!current) return;

    try {
      const locationRef = ref(database, `work/${currentUser.uid}/work_locations/${id}`);
      await set(locationRef, {
        name: current.name,
        color: current.color,
        is_active: isActive
      });
    } catch (error) {
      console.error('Error toggling work location active status:', error);
      setAlertMessage({
        type: 'danger',
        message: 'Fehler beim Aktualisieren des Status.'
      });
    }
  };

  const handleAddLocation = async () => {
    if (!currentUser || !newLocation.name.trim()) return;

    try {
      const locationsRef = ref(database, `work/${currentUser.uid}/work_locations`);
      await push(locationsRef, {
        name: newLocation.name,
        color: newLocation.color,
        is_active: newLocation.is_active
      });

      setNewLocation({ name: '', color: '#1780a6', is_active: true });
      setShowAddModal(false);
      setAlertMessage({
        type: 'success',
        message: 'Arbeitsort erfolgreich hinzugefügt.'
      });
      setTimeout(() => setAlertMessage(null), 3000);
    } catch (error) {
      console.error('Error adding work location:', error);
      setAlertMessage({
        type: 'danger',
        message: 'Fehler beim Hinzufügen des Arbeitsortes.'
      });
    }
  };

  const handleUpdateLocation = async () => {
    if (!currentUser || !editingLocation) return;

    try {
      const locationRef = ref(database, `work/${currentUser.uid}/work_locations/${editingLocation.id}`);
      await set(locationRef, {
        name: editingLocation.name,
        color: editingLocation.color,
        is_active: editingLocation.is_active ?? true
      });

      setEditingLocation(null);
      setAlertMessage({
        type: 'success',
        message: 'Arbeitsort erfolgreich aktualisiert.'
      });
      setTimeout(() => setAlertMessage(null), 3000);
    } catch (error) {
      console.error('Error updating work location:', error);
      setAlertMessage({
        type: 'danger',
        message: 'Fehler beim Aktualisieren des Arbeitsortes.'
      });
    }
  };

  const handleDeleteLocation = async (id: string) => {
    if (!currentUser) return;

    if (!window.confirm('Sind Sie sicher, dass Sie diesen Arbeitsort löschen möchten?')) {
      return;
    }

    try {
      const locationRef = ref(database, `work/${currentUser.uid}/work_locations/${id}`);
      await remove(locationRef);

      setAlertMessage({
        type: 'success',
        message: 'Arbeitsort erfolgreich gelöscht.'
      });
      setTimeout(() => setAlertMessage(null), 3000);
    } catch (error) {
      console.error('Error deleting work location:', error);
      setAlertMessage({
        type: 'danger',
        message: 'Fehler beim Löschen des Arbeitsortes.'
      });
    }
  };

  const handleAddTransport = async () => {
    if (!currentUser || !newTransport.trim()) return;
    try {
      const meansRef = ref(database, `work/${currentUser.uid}/work_meansoftransport`);
      await push(meansRef, {
        name: newTransport.trim(),
        vega: false
      });
      setNewTransport('');
      setShowAddTransportModal(false);
      setAlertMessage({ type: 'success', message: 'Transportmittel hinzugefügt.' });
      setTimeout(() => setAlertMessage(null), 3000);
    } catch (error) {
      setAlertMessage({ type: 'danger', message: 'Fehler beim Hinzufügen.' });
    }
  };

  const handleToggleVega = async (transportId: string, currentVegaStatus: boolean) => {
    if (!currentUser) return;
    
    try {
      const transportRef = ref(database, `work/${currentUser.uid}/work_meansoftransport/${transportId}`);
      await set(transportRef, {
        name: meansOfTransport.find(t => t.id === transportId)?.name || '',
        vega: !currentVegaStatus
      });
    } catch (error) {
      console.error('Error toggling VeGa status:', error);
    }
  };

  const handleUpdateTransport = async () => {
    if (!currentUser || !editingTransport) return;

    try {
      const transportRef = ref(database, `work/${currentUser.uid}/work_meansoftransport/${editingTransport.id}`);
      await set(transportRef, {
        name: editingTransport.name,
        vega: editingTransport.vega
      });

      setEditingTransport(null);
      setAlertMessage({
        type: 'success',
        message: 'Transportmittel erfolgreich aktualisiert.'
      });
      setTimeout(() => setAlertMessage(null), 3000);
    } catch (error) {
      console.error('Error updating transport means:', error);
      setAlertMessage({
        type: 'danger',
        message: 'Fehler beim Aktualisieren des Transportmittels.'
      });
    }
  };

  const handleDeleteTransport = async (transportId: string) => {
    if (!currentUser) return;

    if (!window.confirm('Sind Sie sicher, dass Sie dieses Transportmittel löschen möchten?')) {
      return;
    }

    try {
      const transportRef = ref(database, `work/${currentUser.uid}/work_meansoftransport/${transportId}`);
      await remove(transportRef);
      setAlertMessage({ type: 'success', message: 'Transportmittel gelöscht.' });
      setTimeout(() => setAlertMessage(null), 3000);
    } catch (error) {
      setAlertMessage({ type: 'danger', message: 'Fehler beim Löschen.' });
    }
  };

  if (!currentUser) {
    return (
      <Container>
        <h2 className="mb-4">Arbeitsorte</h2>
        <Card>
          <Card.Body>
            <Card.Title style={{ color: '#1780a6' }}>Arbeitsorte Konfiguration</Card.Title>
            <p>Bitte melden Sie sich an, um die Einstellungen anzuzeigen.</p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <h2 className="mb-4">Arbeitsorte</h2>

      <Nav variant="tabs" className="mb-4">
        <Nav.Item>
          <Nav.Link as={Link} to="/dashboard/work" active={location.pathname === '/dashboard/work'}>
            Work Dashboard
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link as={Link} to="/dashboard/work-settings" active={location.pathname === '/dashboard/work-settings'}>
            Konfiguration
          </Nav.Link>
        </Nav.Item>
      </Nav>

      {alertMessage && (
        <Alert variant={alertMessage.type} onClose={() => setAlertMessage(null)} dismissible className="mb-4">
          {alertMessage.message}
        </Alert>
      )}

      <Card className="mb-4">
        <Card.Body>
          <Card.Title style={{ color: '#1780a6' }} className="d-flex justify-content-between align-items-center">
            <span>Verfügbare Arbeitsorte</span>
            <Button variant="primary" size="sm" onClick={() => setShowAddModal(true)}>
              Neuen Arbeitsort hinzufügen
            </Button>
          </Card.Title>

          {workLocations.length > 0 ? (
            <Table responsive hover className="mt-3">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Farbe</th>
                  <th>Aktiv</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {workLocations.map(location => (
                  <tr key={location.id}>
                    <td>{location.name}</td>
                    <td>
                      <div
                        style={{
                          backgroundColor: location.color,
                          width: '30px',
                          height: '30px',
                          borderRadius: '4px'
                        }}
                      />
                    </td>
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={location.is_active !== false}
                        onChange={(e) => handleToggleActive(location.id, e.target.checked)}
                      />
                    </td>
                    <td>
                      <Button variant="outline-primary" size="sm" className="me-2" onClick={() => setEditingLocation(location)}>
                        Bearbeiten
                      </Button>
                      <Button variant="outline-danger" size="sm" onClick={() => handleDeleteLocation(location.id)}>
                        Löschen
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-center py-4">
              <p className="text-muted">Keine Arbeitsorte konfiguriert</p>
              <Button variant="primary" onClick={() => setShowAddModal(true)}>
                Ersten Arbeitsort hinzufügen
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Neuen Arbeitsort hinzufügen</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={newLocation.name}
                onChange={(e) => setNewLocation({ ...newLocation, name: e.target.value })}
                placeholder="z.B. Büro, Home Office, Kunde"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Farbe</Form.Label>
              <Form.Control
                type="color"
                value={newLocation.color}
                onChange={(e) => setNewLocation({ ...newLocation, color: e.target.value })}
                title="Wählen Sie eine Farbe"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Aktiv"
                checked={newLocation.is_active}
                onChange={(e) => setNewLocation({ ...newLocation, is_active: e.target.checked })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={handleAddLocation}>
            Hinzufügen
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!editingLocation} onHide={() => setEditingLocation(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Arbeitsort bearbeiten</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingLocation && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  value={editingLocation.name}
                  onChange={(e) => setEditingLocation({ ...editingLocation, name: e.target.value })}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Farbe</Form.Label>
                <Form.Control
                  type="color"
                  value={editingLocation.color}
                  onChange={(e) => setEditingLocation({ ...editingLocation, color: e.target.value })}
                  title="Wählen Sie eine Farbe"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Aktiv"
                  checked={editingLocation.is_active !== false}
                  onChange={(e) => setEditingLocation({ ...editingLocation, is_active: e.target.checked })}
                />
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditingLocation(null)}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={handleUpdateLocation}>
            Speichern
          </Button>
        </Modal.Footer>
      </Modal>

      <Card className="mb-4">
        <Card.Body>
          <Card.Title style={{ color: '#1780a6' }} className="d-flex justify-content-between align-items-center">
            <span>Verfügbare Arbeitswege</span>
            <Button variant="primary" size="sm" onClick={() => setShowAddTransportModal(true)}>
              Neues Transportmittel hinzufügen
            </Button>
          </Card.Title>
          {meansOfTransport.length > 0 ? (
            <Table responsive hover className="mt-3">
              <thead>
                <tr>
                  <th>Transportmittel</th>
                  <th>VeGa</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {meansOfTransport.map((transport) => (
                  <tr key={transport.id}>
                    <td>{transport.name}</td>
                    <td>
                      <Form.Check
                        type="checkbox"
                        label=""
                        checked={transport.vega}
                        onChange={() => handleToggleVega(transport.id, transport.vega)}
                        id={`vega-checkbox-${transport.id}`}
                      />
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <Button 
                          variant="outline-primary" 
                          size="sm" 
                          onClick={() => setEditingTransport(transport)}
                        >
                          Bearbeiten
                        </Button>
                        <Button 
                          variant="outline-danger" 
                          size="sm" 
                          onClick={() => handleDeleteTransport(transport.id)}
                        >
                          Löschen
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-center py-4">
              <p className="text-muted">Keine Transportmittel konfiguriert</p>
              <Button variant="primary" onClick={() => setShowAddTransportModal(true)}>
                Erstes Transportmittel hinzufügen
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal show={showAddTransportModal} onHide={() => setShowAddTransportModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Neues Transportmittel hinzufügen</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Transportmittel</Form.Label>
              <Form.Control
                type="text"
                value={newTransport}
                onChange={(e) => setNewTransport(e.target.value)}
                placeholder="z.B. Auto, Fahrrad, ÖV, zu Fuß"
                required
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddTransportModal(false)}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={handleAddTransport}>
            Hinzufügen
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Transport Modal */}
      <Modal show={editingTransport !== null} onHide={() => setEditingTransport(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Transportmittel bearbeiten</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingTransport && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  value={editingTransport.name}
                  onChange={(e) => setEditingTransport({ ...editingTransport, name: e.target.value })}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="VeGa"
                  checked={editingTransport.vega}
                  onChange={(e) => setEditingTransport({ ...editingTransport, vega: e.target.checked })}
                  id="edit-vega-checkbox"
                />
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditingTransport(null)}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={handleUpdateTransport}>
            Speichern
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default WorkSettings;
